import type { FC, ReactNode } from 'react'
import type {
    Device,
    InventoryData,
    Rack
} from '@/types'
import type { InventoryStats } from '@/lib/inventory'
import type { HallLayout } from '@/components/datacenter/layout'

import { createContext, useContext, useMemo } from 'react'
import { computeStats, groupDevicesByRack } from '@/lib/inventory'
import { buildHallLayout } from '@/components/datacenter/layout'

export type InventoryContextValue = {
    inventory: InventoryData
    layout: HallLayout
    stats: InventoryStats
    rackById: Map<string, Rack>
    deviceById: Map<string, Device>
    devicesByRack: Map<string, Device[]>
}

const InventoryContext = createContext<InventoryContextValue | null>(null)

interface InventoryProviderProps {
    inventory: InventoryData
    children: ReactNode
}

export const InventoryProvider: FC<InventoryProviderProps> = ({ inventory, children }): ReactNode => {
    const value = useMemo<InventoryContextValue>(() => ({
        inventory,
        layout: buildHallLayout(inventory.racks, inventory.devices),
        stats: computeStats(inventory.racks, inventory.devices, inventory.facility),
        rackById: new Map(inventory.racks.map(rack => [rack.id, rack])),
        deviceById: new Map(inventory.devices.map(device => [device.id, device])),
        devicesByRack: groupDevicesByRack(inventory.devices)
    }), [inventory])

    return (
        <InventoryContext.Provider value={value}>
            {children}
        </InventoryContext.Provider>
    )
}

export const useInventory = (): InventoryContextValue => {
    const value = useContext(InventoryContext)

    if (!value) throw new Error('useInventory must be used inside InventoryProvider')

    return value
}
